import React from "react";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ChevronUp, ChevronDown, Trash2, Copy } from "lucide-react";

const key = (it: any) => {
  if (!it._k) Object.defineProperty(it, "_k", { value: Math.random().toString(36).slice(2), enumerable: false });
  return it._k;
};

function Row({ id, index, count, onMove, onRemove, onDuplicate, children, testId }: any) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={testId}
      className={`flex gap-2 bg-white border ${isDragging ? "border-neutral-900 shadow-lg z-10 relative" : "border-neutral-200"}`}>
      <button ref={setActivatorNodeRef} {...attributes} {...listeners} aria-label="Drag to reorder" data-testid={`${testId}-handle`}
        className="px-2 text-neutral-400 hover:text-neutral-900 cursor-grab active:cursor-grabbing touch-none"><GripVertical size={16} /></button>
      <div className="flex-1 min-w-0 py-2">{children}</div>
      <div className="flex flex-col justify-center pr-1">
        <button disabled={index === 0} onClick={() => onMove(index, index - 1)} aria-label="Move up" className="p-1 disabled:opacity-20 hover:text-[var(--c-primary)]" data-testid={`${testId}-up`}><ChevronUp size={14} /></button>
        <button disabled={index === count - 1} onClick={() => onMove(index, index + 1)} aria-label="Move down" className="p-1 disabled:opacity-20 hover:text-[var(--c-primary)]" data-testid={`${testId}-down`}><ChevronDown size={14} /></button>
      </div>
      <div className="flex flex-col justify-center pr-2 border-l border-neutral-100 pl-1">
        {onDuplicate && <button onClick={() => onDuplicate(index)} aria-label="Duplicate" className="p-1 hover:text-[var(--c-primary)]" data-testid={`${testId}-duplicate`}><Copy size={13} /></button>}
        <button onClick={() => onRemove(index)} aria-label="Remove" className="p-1 hover:text-red-700" data-testid={`${testId}-remove`}><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

export function SortableList({ items, onChange, render, testId, duplicate = false, className = "space-y-2" }: {
  items: any[]; onChange: (v: any[]) => void; render: (item: any, update: (patch: any) => void, index: number) => React.ReactNode; testId: string; duplicate?: boolean; className?: string;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const list = items || [];
  const ids = list.map(key);
  const move = (from: number, to: number) => onChange(arrayMove(list, from, to));
  const update = (i: number, patch: any) => onChange(list.map((it, n) => (n === i ? Object.assign(Object.defineProperty({ ...it, ...patch }, "_k", { value: it._k, enumerable: false })) : it)));
  const remove = (i: number) => { if (window.confirm("Remove this item?")) onChange(list.filter((_, n) => n !== i)); };
  const dup = (i: number) => onChange([...list.slice(0, i + 1), JSON.parse(JSON.stringify(list[i])), ...list.slice(i + 1)]);
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={({ active, over }) => {
      if (over && active.id !== over.id) move(ids.indexOf(active.id as string), ids.indexOf(over.id as string));
    }}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={className} data-testid={testId}>
          {list.map((it, i) => (
            <Row key={ids[i]} id={ids[i]} index={i} count={list.length} onMove={move} onRemove={remove} onDuplicate={duplicate ? dup : null} testId={`${testId}-item-${i}`}>
              {render(it, (patch) => update(i, patch), i)}
            </Row>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export const miniInput = "w-full border border-neutral-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-neutral-900";

export function SecondsInput({ value, onChange, min = 1, max = 30, step = 1, def, testId, className = "!w-20" }: { value?: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; def: number; testId: string; className?: string }) {
  const [txt, setTxt] = React.useState<string | null>(null);
  const commit = () => { if (txt === null) return; const n = Number(txt); onChange(txt === "" || isNaN(n) ? def : Math.min(max, Math.max(min, n))); setTxt(null); };
  return <input type="number" inputMode="decimal" min={min} max={max} step={step} value={txt ?? String(value ?? def)} onChange={(e) => { setTxt(e.target.value); const n = Number(e.target.value); if (e.target.value !== "" && n >= min && n <= max) onChange(n); }} onBlur={commit} className={`${miniInput} ${className}`} data-testid={testId} />;
}
